/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  Eye,
  TrendingUp,
  CheckCircle,
  Clock,
  ArrowUpRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import axiosClient from '../api/axiosClient';

const DEFAULT_TRAFFIC = [
  { day: 'Mon', visitors: 140, bookings: 3 },
  { day: 'Tue', visitors: 220, bookings: 5 },
  { day: 'Wed', visitors: 310, bookings: 8 },
  { day: 'Thu', visitors: 280, bookings: 6 },
  { day: 'Fri', visitors: 390, bookings: 9 },
  { day: 'Sat', visitors: 190, bookings: 4 },
  { day: 'Sun', visitors: 160, bookings: 2 },
];

const DEVICE_DATA = [
  { name: 'Desktop', value: 68, color: '#3b82f6' },
  { name: 'Mobile', value: 27, color: '#10b981' },
  { name: 'Tablet', value: 5, color: '#a855f7' },
];

const TOP_PAGES = [
  { path: '/', title: 'Home Page', views: 4120, percentage: 38 },
  { path: '/services', title: 'IT Services Overview', views: 2450, percentage: 22 },
  { path: '/services/cybersecurity-services', title: 'Cybersecurity & SOC', views: 1890, percentage: 17 },
  { path: '/book-consultation', title: 'Book a Consultation', views: 1430, percentage: 13 },
  { path: '/about', title: 'About FAJTech', views: 1120, percentage: 10 },
];

const TOP_COUNTRIES = [
  { country: 'United States', flag: '🇺🇸', sessions: '62%' },
  { country: 'United Kingdom', flag: '🇬🇧', sessions: '14%' },
  { country: 'Canada', flag: '🇨🇦', sessions: '11%' },
  { country: 'Germany', flag: '🇩🇪', sessions: '7%' },
  { country: 'Other', flag: '🌐', sessions: '6%' },
];

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axiosClient
      .get('/analytics/dashboard')
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  const chartData = stats?.trafficTrends?.length ? stats.trafficTrends : DEFAULT_TRAFFIC;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
          <BarChart3 className="text-blue-500" />
          <span>Analytics & Traffic Intelligence</span>
        </h1>
        <p className="text-xs text-slate-400 mt-1">
          Monitor visitor traffic, device breakdowns, top landing pages, and lead conversion rates.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase tracking-wider">
            <span>Total Unique Visitors</span>
            <Eye size={18} className="text-blue-400" />
          </div>
          <p className="text-3xl font-extrabold text-white mt-3">
            {(stats?.counts?.visitors || 1690).toLocaleString()}
          </p>
          <span className="text-[11px] text-emerald-400 font-semibold flex items-center space-x-1 mt-1">
            <TrendingUp size={12} />
            <span>+18.4% vs last month</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase tracking-wider">
            <span>Lead Conversion Rate</span>
            <CheckCircle size={18} className="text-emerald-400" />
          </div>
          <p className="text-3xl font-extrabold text-emerald-400 mt-3">4.82%</p>
          <span className="text-[11px] text-slate-400 mt-1 block">37 Total Consultations</span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase tracking-wider">
            <span>Avg Session Duration</span>
            <Clock size={18} className="text-purple-400" />
          </div>
          <p className="text-3xl font-extrabold text-purple-400 mt-3">3m 45s</p>
          <span className="text-[11px] text-emerald-400 font-semibold flex items-center space-x-1 mt-1">
            <TrendingUp size={12} />
            <span>+12.1% engagement</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase tracking-wider">
            <span>Bounce Rate</span>
            <ArrowUpRight size={18} className="text-amber-400" />
          </div>
          <p className="text-3xl font-extrabold text-white mt-3">28.4%</p>
          <span className="text-[11px] text-emerald-400 font-semibold mt-1 block">-3.2% optimal range</span>
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-base text-white">Daily Traffic & Bookings Volume</h3>
            <p className="text-xs text-slate-400">Visitor volume (Blue) correlated with consultation submissions (Green)</p>
          </div>
        </div>

        <div className="h-80 w-full pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="bookingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: '#020617', borderColor: '#1e293b', borderRadius: '12px', color: '#fff' }} />
              <Area type="monotone" dataKey="visitors" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#visitorGradient)" name="Visitors" />
              <Area type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#bookingGradient)" name="Bookings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdown Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Landing Pages */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
          <h3 className="font-bold text-sm text-white">Top Visited Landing Pages</h3>
          <div className="space-y-3">
            {TOP_PAGES.map((page, idx) => (
              <div key={idx} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-white">{page.title} <span className="text-slate-500 font-mono">({page.path})</span></span>
                  <span className="text-slate-400 font-mono">{page.views.toLocaleString()} views</span>
                </div>
                <div className="w-full bg-slate-950 rounded-full h-2 overflow-hidden border border-slate-800">
                  <div
                    className="bg-blue-600 h-full rounded-full transition-all duration-500"
                    style={{ width: `${page.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Devices & Countries */}
        <div className="space-y-6">
          {/* Devices */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
            <h3 className="font-bold text-sm text-white">Device Breakdown</h3>
            <div className="space-y-3">
              {DEVICE_DATA.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-slate-300 font-medium">{d.name}</span>
                  </div>
                  <span className="font-bold text-white font-mono">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Countries */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
            <h3 className="font-bold text-sm text-white">Geographic Distribution</h3>
            <div className="space-y-2.5">
              {TOP_COUNTRIES.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    <span>{c.flag}</span>
                    <span className="text-slate-300">{c.country}</span>
                  </div>
                  <span className="font-bold text-blue-400 font-mono">{c.sessions}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
